<template>
  <div :class="componentClassName">
    <div class="t-default-layout__nav">
      <o-global-sidebar
        :key="sidebarKey"
        :navigation-items="navigationItems"
        :current-user-info="currentUserInfo"
        :handle-online-status-change="handleOnlineStatusChange"
        :handle-logout="handleLogout"
        :username="username"
        :company-i-d="companyID"
      ></o-global-sidebar>
    </div>
    <div class="t-default-layout__main">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { mapModifiers } from '@/libs/component';
import { SERVICE_TERMINATION_NAVIGATION_ITEMS } from '@/libs/constants';
import { POINT } from '@/libs/constants';
import { cloneDeep } from 'lodash';

import { utils } from '@/common/utils.js';

export default {
  name: 't-default-layout',
  props: {
    class: {
      type: String,
      default: '',
    },
    handleOnlineStatusChange: {
      type: Function,
      default: () => {},
    },
    handleLogout: {
      type: Function,
      default: () => {},
    },
    username: {
      type: String,
      default: '',
    },
    companyID: {
      type: String,
      default: '',
    },
    currentUserInfo: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      resultModalTitle: '',
      resultModalContent: '',
      resultModalVisible: false,
      voiceToTextOn: false,
      navigationItems: [],
      sidebarKey: 0,
    };
  },
  computed: {
    componentClassName() {
      const baseClassName = mapModifiers('t-default-layout');
      return `${baseClassName} ${this.class}`.trim();
    },
    isComUser() {
      return this.currentUserInfo.company_id === POINT;
    },
    isGeneralUser() {
      return this.currentUserInfo.company_id !== POINT;
    },
    isManager() {
      return this.currentUserInfo.auth_info === 'manager';
    },
    isOperator() {
      return this.currentUserInfo.auth_info === 'operator';
    },
    isSoOperator() {
      return this.currentUserInfo.auth_info === 'soOperator';
    },
    isComOperator() {
      return this.isComUser && this.isOperator;
    },
    isComSoOperator() {
      return this.isComUser && this.isSoOperator;
    },
    isGeneralOperator() {
      return this.isGeneralUser && this.isOperator;
    },
  },
  watch: {},
  created() {
    this.navigationItems = this.getNavigationItems();
  },
  methods: {
    getNavigationItems() {
      const navigationItems = cloneDeep(SERVICE_TERMINATION_NAVIGATION_ITEMS);
      if (this.isGeneralUser) {
        const navigationItemsIndex = navigationItems.findIndex(({ nav_id }) => nav_id === 'settings');
        navigationItems[navigationItemsIndex].items = navigationItems[navigationItemsIndex].items.filter(
          item =>
            item.nav_id !== 'settings-company-list' &&
            item.nav_id !== 'settings-request' &&
            // item.nav_id !== 'price-history-list-mgmt' &&
            item.nav_id !== 'number-mgmt' &&
            item.nav_id !== 'number-mgmt-fdnd' // #3112
        );
      }

      // メニュー表示判別処理
      for (let i = 0; i < navigationItems.length; i++) {
        // 発着信履歴
        if (navigationItems[i].nav_id === 'call_history') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // 連絡先
        if (navigationItems[i].nav_id === 'contact') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // アウトバウンド
        if (navigationItems[i].nav_id === 'outbound-list') {
          if (this.isSoOperator) {
            navigationItems.splice(i, 1);
          }
        }

        // 設定
        if (navigationItems[i].nav_id === 'settings') {
          navigationItems[i] = this.processSettingNavItems(navigationItems[i]);
        }
      }

      return navigationItems;
    },
    processSettingNavItems(settings) {
      for (let j = 0; j < settings.items.length; j++) {
        // 料金状況
        if (settings.items[j].nav_id === 'price-history-list') {
          // 003300のオペレーター権限（SO）と003300以外のオペレーター権限は非表示
          if (this.isComSoOperator || this.isGeneralOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 料金状況管理
        if (settings.items[j].nav_id === 'price-history-list-mgmt') {
          // 003300のオペレーター権限と003300以外は非表示
          if (this.isComOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 営業時間設定
        if (settings.items[j].nav_id === 'settings-business-hours') {
          // 003300のオペレーター権限（SO）は非表示
          if (this.isComSoOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 着信フロー設定
        if (settings.items[j].nav_id === 'settings-incoming-flows') {
          // 003300のオペレーター権限（SO）は非表示
          if (this.isComSoOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 通録テキスト化設定(PoC対応)
        if (settings.items[j].nav_id === 'settings-voice-to-text') {
          // トライアルアカウントまたは通録テキスト化機能がOFFの場合は非表示
          if (utils.chkTrialCompanyID(this.companyID) || !this.voiceToTextOn) {
            settings.items.splice(j, 1);
          }
        }

        // 契約情報確認・変更
        if (settings.items[j].nav_id === 'settings-contract') {
          // 003300のオペレーター権限（SO）と003300以外のオペレーター権限は非表示
          if (this.isComSoOperator || this.isGeneralOperator) {
            settings.items.splice(j, 1);
          }
        }

        // 企業情報管理
        if (settings.items[j].nav_id === 'settings-company-list') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 申し込み管理
        if (settings.items[j].nav_id === 'settings-request') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 電話番号管理
        if (settings.items[j].nav_id === 'number-mgmt') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // #3112
        // FD/ND番号管理
        if (settings.items[j].nav_id === 'number-mgmt-fdnd') {
          // 003300のオペレーター権限と003300以外は非表示
          if (this.isComOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // 電話番号管理(一般ユーザー用)
        if (settings.items[j].nav_id === 'number-mgmt-general') {
          // オペレーター権限と003300は非表示
          // #3445 トライアルユーザも非表示
          if (this.isComUser || this.isOperator || utils.chkTrialCompanyID(this.currentUserInfo.company_id)) {
            settings.items.splice(j, 1);
          }
        }

        // #2945
        // トライアル管理
        if (settings.items[j].nav_id === 'settings-trial') {
          // 003300以外は非表示
          if (this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }

        // メール配信管理
        if (settings.items[j].nav_id === 'settings-sendmail') {
          // 003300のSOオペレーター権限と003300以外は非表示
          if (this.isComSoOperator || this.isGeneralUser) {
            settings.items.splice(j, 1);
          }
        }
      }
      return settings;
    },
  },
};
</script>
